/* YourXI "See it on your wall" preview.
 *
 *   const view = YXIWallView.mount(el, {
 *     getPreviewCanvas: () => canvas | img | Blob | Promise<...>,   // or previewDataURL: "data:..." / any image URL
 *     frame: "black" | "white",
 *     size: "12x16" | "18x24",
 *     scene: "hero" | "desk" | "white" | "size",                    // optional, default "hero"
 *     onClose: () => {},                                            // optional; shows a close button, Esc closes
 *     assetsBase: "modules/wallview-scenes/"                        // optional, default: next to this script
 *   });
 *   view.update({frame, size, scene, getPreviewCanvas, previewDataURL, refreshPrint:true});
 *   view.destroy();
 *   YXIWallView.preload();   // optional: warm the scene cache before the first open
 *
 * The customer's print is warped into each room's frame window on the GPU (WebGL homography, per-pixel, no seams),
 * then the room's light map is multiplied over it and the acrylic sheen screened on top, exactly as
 * imagery/composite.py does for the product photos. The print is fitted whole into the window (A3 in a 3:4 frame
 * leaves thin white paper strips at the sides, like the real product). Canvas 2D fallback if WebGL is missing.
 */
(function () {
  "use strict";
  const SCRIPT_SRC = (document.currentScript && document.currentScript.src) || location.href;
  const DEFAULT_BASE = new URL("wallview-scenes/", SCRIPT_SRC).href;
  const ORDER = ["hero", "desk", "white", "size"];
  const SIZE_INFO = { "12x16": { inch: "12\u00a0x\u00a016\u00a0in", cm: "30\u00a0x\u00a041\u00a0cm" }, "18x24": { inch: "18\u00a0x\u00a024\u00a0in", cm: "46\u00a0x\u00a061\u00a0cm" } };

  /* ---------------- shared caches (survive mount/destroy) */
  const cache = { json: new Map(), img: new Map() };
  function loadJSON(base) {
    if (!cache.json.has(base)) cache.json.set(base, fetch(base + "scenes.json").then(r => { if (!r.ok) throw new Error("scenes.json " + r.status); return r.json(); }));
    return cache.json.get(base);
  }
  function loadImg(url) {
    if (!cache.img.has(url)) {
      cache.img.set(url, new Promise((res, rej) => {
        const im = new Image(); im.decoding = "async"; im.crossOrigin = "anonymous";
        im.onload = () => (im.decode ? im.decode().catch(() => {}) : Promise.resolve()).then(() => res(im));
        im.onerror = () => { cache.img.delete(url); rej(new Error("image failed: " + url)); };
        im.src = url;
      }));
    }
    return cache.img.get(url);
  }

  /* ---------------- maths */
  function squareToQuad(q) { // unit square -> quad (TL,TR,BR,BL), returns row-major 3x3
    const [x0, y0] = q[0], [x1, y1] = q[1], [x2, y2] = q[2], [x3, y3] = q[3];
    const dx1 = x1 - x2, dx2 = x3 - x2, dy1 = y1 - y2, dy2 = y3 - y2;
    const sx = x0 - x1 + x2 - x3, sy = y0 - y1 + y2 - y3;
    let g = 0, h = 0;
    if (Math.abs(sx) > 1e-9 || Math.abs(sy) > 1e-9) {
      const den = dx1 * dy2 - dx2 * dy1;
      g = (sx * dy2 - dx2 * sy) / den; h = (dx1 * sy - sx * dy1) / den;
    }
    return [x1 - x0 + g * x1, x3 - x0 + h * x3, x0,
            y1 - y0 + g * y1, y3 - y0 + h * y3, y0,
            g, h, 1];
  }
  function inv3(m) {
    const [a, b, c, d, e, f, g, h, i] = m;
    const A = e * i - f * h, B = -(d * i - f * g), C = d * h - e * g;
    const det = a * A + b * B + c * C;
    return [A / det, -(b * i - c * h) / det, (b * f - c * e) / det,
            B / det, (a * i - c * g) / det, -(a * f - c * d) / det,
            C / det, -(a * h - b * g) / det, (a * e - b * d) / det];
  }
  function edgeLines(q) { // inward-positive normalised line equations a*x+b*y+c
    const cx = (q[0][0] + q[1][0] + q[2][0] + q[3][0]) / 4, cy = (q[0][1] + q[1][1] + q[2][1] + q[3][1]) / 4;
    const out = [];
    for (let k = 0; k < 4; k++) {
      const [ax, ay] = q[k], [bx, by] = q[(k + 1) % 4];
      let nx = -(by - ay), ny = bx - ax; const l = Math.hypot(nx, ny); nx /= l; ny /= l;
      let c = -(nx * ax + ny * ay);
      if (nx * cx + ny * cy + c < 0) { nx = -nx; ny = -ny; c = -c; }
      out.push(nx, ny, c);
    }
    return out;
  }
  const pot = v => Math.pow(2, Math.ceil(Math.log2(Math.max(2, v))));
  const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);
  const normFrame = f => (String(f || "black").toLowerCase().indexOf("white") >= 0 ? "white" : "black");
  const normSize = s => { const d = String(s || "").match(/\d+/g); return d && d[0] === "18" ? "18x24" : "12x16"; };

  /* ---------------- print source -> drawable */
  async function resolvePrint(o) {
    let src = null;
    if (typeof o.getPreviewCanvas === "function") src = await o.getPreviewCanvas();
    else if (o.previewDataURL) src = o.previewDataURL;
    if (!src) return null;
    if (typeof src === "string") return loadImgFresh(src);
    if (typeof Blob !== "undefined" && src instanceof Blob) {
      if (window.createImageBitmap) return createImageBitmap(src);
      const url = URL.createObjectURL(src); try { return await loadImgFresh(url); } finally { URL.revokeObjectURL(url); }
    }
    if (src.tagName === "svg" || (window.SVGSVGElement && src instanceof SVGSVGElement)) {
      const xml = new XMLSerializer().serializeToString(src);
      const url = URL.createObjectURL(new Blob([xml], { type: "image/svg+xml" }));
      try { return await loadImgFresh(url); } finally { URL.revokeObjectURL(url); }
    }
    return src; // canvas, img, ImageBitmap
  }
  function loadImgFresh(url) {
    return new Promise((res, rej) => { const im = new Image(); im.crossOrigin = "anonymous"; im.onload = () => res(im); im.onerror = () => rej(new Error("print image failed")); im.src = url; });
  }
  const srcSize = s => [s.naturalWidth || s.videoWidth || s.width, s.naturalHeight || s.videoHeight || s.height];

  /* Fit the whole print into the window's aspect (white paper round it), drawn into a power-of-two canvas so the
     GPU can mipmap it. Stretching to POT is undone by the uv mapping. */
  function buildArt(src, aspect, tw, th) {
    const c = document.createElement("canvas"); c.width = tw; c.height = th;
    const x = c.getContext("2d");
    x.fillStyle = "#fff"; x.fillRect(0, 0, tw, th);
    if (src) {
      const [sw, sh] = srcSize(src); const sa = sw / sh;
      let fw = 1, fh = 1;
      if (sa < aspect) fw = sa / aspect; else fh = aspect / sa;
      // even white border all round: shrink the print until the gap above and below matches the gap at the sides
      // (a narrower print in a wider window would otherwise get strips only at the sides)
      if (sa < aspect && aspect < 1 && sa < 1) {
        const h = (aspect - 1) / (sa - 1);                  // print height as a share of the window height
        if (h > 0.6 && h < 1) { fh = h; fw = sa * h / aspect; }
      } else if (sa > aspect && aspect > 1 && sa > 1) {
        const w = (1 / aspect - 1) / (1 / sa - 1);
        if (w > 0.6 && w < 1) { fw = w; fh = (w * aspect) / sa; }
      }
      x.imageSmoothingEnabled = true; x.imageSmoothingQuality = "high";
      // two-step downscale for very large sources keeps thin type crisp
      let s = src, cw = sw, ch = sh; const dw = fw * tw, dh = fh * th;
      while (cw > dw * 2.2 && ch > dh * 2.2) {
        const t = document.createElement("canvas"); t.width = Math.round(cw / 2); t.height = Math.round(ch / 2);
        const tx = t.getContext("2d"); tx.imageSmoothingQuality = "high"; tx.drawImage(s, 0, 0, t.width, t.height);
        s = t; cw = t.width; ch = t.height;
      }
      x.drawImage(s, (1 - fw) / 2 * tw, (1 - fh) / 2 * th, dw, dh);
    }
    return c;
  }

  /* ---------------- WebGL renderer */
  const VS = "attribute vec2 a;varying vec2 v;void main(){v=a*.5+.5;gl_Position=vec4(a,0.,1.);}";
  const FS = [
    "precision highp float;",
    "varying vec2 v;",
    "uniform sampler2D uBg,uLay,uArt;",
    "uniform vec2 uScene;uniform mat3 uHi;uniform vec3 uE0,uE1,uE2,uE3;uniform vec4 uBox;uniform vec2 uLayPx;",
    "uniform float uVeil,uGrain,uPx,uOn,uKeep;",
    "float hash(vec2 p){p=fract(p*vec2(443.897,441.423));p+=dot(p,p.yx+19.19);return fract((p.x+p.y)*p.x);}",
    "void main(){",
    " vec2 t=vec2(v.x,1.-v.y);",
    " vec3 bg=texture2D(uBg,t).rgb;",
    " if(uOn<.5){gl_FragColor=vec4(bg,1.);return;}",
    " vec3 p=vec3(t*uScene,1.);",
    " float d=min(min(dot(uE0,p),dot(uE1,p)),min(dot(uE2,p),dot(uE3,p)));",
    " float cov=clamp(d/uPx+.85,0.,1.);",       // 0.35 px grow over the paper edge, like the pipeline's 0.6 px
    " if(cov<=0.){if(uKeep>.5)discard;gl_FragColor=vec4(bg,1.);return;}",
    " vec3 h=uHi*p;vec2 uv=clamp(h.xy/h.z,0.,1.);",
    " vec3 art=texture2D(uArt,uv,-.35).rgb;",
    " vec2 lt=clamp((p.xy-uBox.xy)/uBox.zw,vec2(1.)/uLayPx,vec2(1.)-vec2(1.)/uLayPx);",
    " vec3 L=texture2D(uLay,vec2(lt.x*.5,lt.y)).rgb;",
    " vec3 S=texture2D(uLay,vec2(.5+lt.x*.5,lt.y)).rgb;",
    " vec3 o=mix(art*L,L,uVeil);",
    " o=1.-(1.-o)*(1.-S);",
    " o+=(hash(gl_FragCoord.xy)-.5)*uGrain*3.46;",
    " gl_FragColor=vec4(mix(bg,clamp(o,0.,1.),cov),1.);",
    "}"].join("\n");

  function makeGL(canvas) {
    const gl = canvas.getContext("webgl", { alpha: false, antialias: false, premultipliedAlpha: false, preserveDrawingBuffer: true });
    if (!gl) return null;
    const sh = (type, src) => { const s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s); if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s)); return s; };
    const pr = gl.createProgram();
    gl.attachShader(pr, sh(gl.VERTEX_SHADER, VS)); gl.attachShader(pr, sh(gl.FRAGMENT_SHADER, FS)); gl.linkProgram(pr);
    if (!gl.getProgramParameter(pr, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(pr));
    gl.useProgram(pr);
    const buf = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(pr, "a"); gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    const U = {}; ["uBg", "uLay", "uArt", "uScene", "uHi", "uE0", "uE1", "uE2", "uE3", "uBox", "uLayPx", "uVeil", "uGrain", "uPx", "uOn", "uKeep"].forEach(n => U[n] = gl.getUniformLocation(pr, n));
    gl.uniform1i(U.uBg, 0); gl.uniform1i(U.uLay, 1); gl.uniform1i(U.uArt, 2);
    gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE);
    const aniso = gl.getExtension("EXT_texture_filter_anisotropic") || gl.getExtension("WEBKIT_EXT_texture_filter_anisotropic");
    const texs = new Map();
    function tex(unit, key, source, mip) {
      gl.activeTexture(gl.TEXTURE0 + unit);
      let t = texs.get(unit);
      if (t && t.key === key) { gl.bindTexture(gl.TEXTURE_2D, t.tex); return; }
      if (!t) { t = { tex: gl.createTexture() }; texs.set(unit, t); }
      t.key = key;
      gl.bindTexture(gl.TEXTURE_2D, t.tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, source);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      if (mip) {
        gl.generateMipmap(gl.TEXTURE_2D);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
        if (aniso) gl.texParameterf(gl.TEXTURE_2D, aniso.TEXTURE_MAX_ANISOTROPY_EXT, Math.min(8, gl.getParameter(aniso.MAX_TEXTURE_MAX_ANISOTROPY_EXT)));
      } else gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    }
    return {
      draw(job) {
        gl.viewport(0, 0, canvas.width, canvas.height);
        tex(0, job.bgKey, job.bg, false);
        gl.uniform2f(U.uScene, job.sw, job.sh);
        if (job.win) {
          const w = job.win;
          tex(1, w.layKey, w.lay, false);
          tex(2, job.artKey, job.art, true);
          const hi = w.hinv; // row-major -> column-major for GLSL
          gl.uniformMatrix3fv(U.uHi, false, new Float32Array([hi[0], hi[3], hi[6], hi[1], hi[4], hi[7], hi[2], hi[5], hi[8]]));
          const e = w.edges;
          gl.uniform3f(U.uE0, e[0], e[1], e[2]); gl.uniform3f(U.uE1, e[3], e[4], e[5]);
          gl.uniform3f(U.uE2, e[6], e[7], e[8]); gl.uniform3f(U.uE3, e[9], e[10], e[11]);
          gl.uniform4f(U.uBox, w.bbox[0], w.bbox[1], w.bbox[2], w.bbox[3]);
          gl.uniform2f(U.uLayPx, w.bbox[2], w.bbox[3]);
          gl.uniform1f(U.uVeil, w.veil); gl.uniform1f(U.uGrain, w.grain);
          gl.uniform1f(U.uPx, job.sw / canvas.width);
          gl.uniform1f(U.uOn, 1);
        } else gl.uniform1f(U.uOn, 0);
        gl.uniform1f(U.uKeep, job.keep ? 1 : 0);   // keep: a second window on the same scene, leave the rest as drawn
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      },
      lost: () => gl.isContextLost(),
      destroy() { texs.forEach(t => gl.deleteTexture(t.tex)); const l = gl.getExtension("WEBGL_lose_context"); if (l) l.loseContext(); }
    };
  }

  /* ---------------- Canvas 2D fallback: triangle-subdivided warp + multiply/screen layers */
  function make2D(canvas) {
    const ctx = canvas.getContext("2d");
    return {
      draw(job) {
        const s = canvas.width / job.sw;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.globalCompositeOperation = "source-over";
        if (!job.keep) ctx.drawImage(job.bg, 0, 0, canvas.width, canvas.height);
        if (!job.win) return;
        const w = job.win, q = w.q.map(p => [p[0] * s, p[1] * s]), H = squareToQuad(q);
        const map = (u, v) => { const d = H[6] * u + H[7] * v + 1; return [(H[0] * u + H[1] * v + H[2]) / d, (H[3] * u + H[4] * v + H[5]) / d]; };
        const N = 16, A = job.art, aw = A.width, ah = A.height;
        ctx.save(); ctx.beginPath(); q.forEach((p, i) => i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1])); ctx.closePath(); ctx.clip();
        for (let j = 0; j < N; j++) for (let i = 0; i < N; i++) {
          const u0 = i / N, u1 = (i + 1) / N, v0 = j / N, v1 = (j + 1) / N;
          tri([u0, v0], [u1, v0], [u0, v1]); tri([u1, v0], [u1, v1], [u0, v1]);
        }
        function tri(a, b, c) {
          const pa = map(a[0], a[1]), pb = map(b[0], b[1]), pc = map(c[0], c[1]);
          const cx = (pa[0] + pb[0] + pc[0]) / 3, cy = (pa[1] + pb[1] + pc[1]) / 3, g = 0.6; // grow 0.6 px: no seams
          const grow = p => { const dx = p[0] - cx, dy = p[1] - cy, l = Math.hypot(dx, dy) || 1; return [p[0] + dx / l * g, p[1] + dy / l * g]; };
          const A2 = grow(pa), B2 = grow(pb), C2 = grow(pc);
          const sx0 = a[0] * aw, sy0 = a[1] * ah, sx1 = b[0] * aw, sy1 = b[1] * ah, sx2 = c[0] * aw, sy2 = c[1] * ah;
          const den = (sx1 - sx0) * (sy2 - sy0) - (sx2 - sx0) * (sy1 - sy0);
          const m11 = ((pb[0] - pa[0]) * (sy2 - sy0) - (pc[0] - pa[0]) * (sy1 - sy0)) / den;
          const m12 = ((pb[1] - pa[1]) * (sy2 - sy0) - (pc[1] - pa[1]) * (sy1 - sy0)) / den;
          const m21 = ((pc[0] - pa[0]) * (sx1 - sx0) - (pb[0] - pa[0]) * (sx2 - sx0)) / den;
          const m22 = ((pc[1] - pa[1]) * (sx1 - sx0) - (pb[1] - pa[1]) * (sx2 - sx0)) / den;
          ctx.save(); ctx.beginPath(); ctx.moveTo(A2[0], A2[1]); ctx.lineTo(B2[0], B2[1]); ctx.lineTo(C2[0], C2[1]); ctx.closePath(); ctx.clip();
          ctx.setTransform(m11, m12, m21, m22, pa[0] - m11 * sx0 - m21 * sy0, pa[1] - m12 * sx0 - m22 * sy0);
          ctx.drawImage(A, 0, 0); ctx.restore();
        }
        const L = w.lay, bw = w.bbox[2], bh = w.bbox[3], bx = w.bbox[0] * s, by = w.bbox[1] * s;
        ctx.globalCompositeOperation = "multiply"; ctx.drawImage(L, 0, 0, bw, bh, bx, by, bw * s, bh * s);
        ctx.globalCompositeOperation = "screen"; ctx.drawImage(L, bw, 0, bw, bh, bx, by, bw * s, bh * s);
        ctx.restore(); ctx.globalCompositeOperation = "source-over";
      },
      lost: () => false, destroy() {}
    };
  }

  /* ---------------- UI */
  const ICON_X = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';

  function mount(el, opts) {
    if (!el) throw new Error("YXIWallView.mount: no element");
    const o = Object.assign({ frame: "black", size: "12x16", scene: "hero" }, opts || {});
    o.frame = normFrame(o.frame); o.size = normSize(o.size);
    const base = o.assetsBase ? new URL(o.assetsBase, location.href).href : DEFAULT_BASE;
    let data = null, print = null, printVer = 0, renderer = null, dead = false, raf = 0, seq = 0;
    const artCache = new Map(), bgCache = new Map();

    el.classList.add("yxw");
    el.innerHTML =
      '<div class="yxw-head"><div class="yxw-title">See it on your wall</div>' +
      (o.onClose ? '<button type="button" class="yxw-close" aria-label="Close">' + ICON_X + "</button>" : "") + "</div>" +
      '<div class="yxw-body"><div class="yxw-stage" data-state="loading"><canvas class="yxw-canvas" role="img"></canvas>' +
      '<div class="yxw-labels" aria-hidden="true"></div><div class="yxw-status" role="status" aria-live="polite"></div></div>' +
      '<div class="yxw-side"><p class="yxw-cap"></p><div class="yxw-scenes" role="radiogroup" aria-label="Room"></div>' +
      '<p class="yxw-meta"></p></div></div>';
    const $ = s => el.querySelector(s);
    const stage = $(".yxw-stage"), canvas = $(".yxw-canvas"), labels = $(".yxw-labels"), status = $(".yxw-status");
    const scenesEl = $(".yxw-scenes"), cap = $(".yxw-cap"), meta = $(".yxw-meta");
    const closeBtn = $(".yxw-close");
    if (closeBtn) closeBtn.addEventListener("click", () => o.onClose && o.onClose());
    const onKey = e => { if (e.key === "Escape" && o.onClose && el.contains(document.activeElement)) o.onClose(); };
    el.addEventListener("keydown", onKey);

    function setStatus(state, text) { stage.dataset.state = state; status.textContent = text || ""; }

    function sceneById(id) { return data.scenes.find(s => s.id === id) || data.scenes[0]; }

    function buildThumbs() {
      scenesEl.innerHTML = "";
      ORDER.map(id => data.scenes.find(s => s.id === id)).filter(Boolean).forEach(s => {
        const b = document.createElement("button");
        b.type = "button"; b.className = "yxw-scene"; b.dataset.id = s.id; b.setAttribute("role", "radio");
        b.innerHTML = '<span class="yxw-thumb"></span><span class="yxw-sname"></span>';
        const im = new Image(96, 120); im.alt = ""; im.decoding = "async"; im.src = base + s.thumb[o.frame];
        b.querySelector(".yxw-thumb").appendChild(im);
        b.querySelector(".yxw-sname").textContent = s.label;
        b.addEventListener("click", () => { if (o.scene !== s.id) { o.scene = s.id; syncUI(); render(); } });
        b.addEventListener("keydown", e => {
          const k = e.key; if (k !== "ArrowRight" && k !== "ArrowLeft" && k !== "ArrowDown" && k !== "ArrowUp") return;
          e.preventDefault(); const all = [...scenesEl.children]; const i = all.indexOf(b);
          const n = all[(i + (k === "ArrowRight" || k === "ArrowDown" ? 1 : all.length - 1)) % all.length]; n.focus(); n.click();
        });
        scenesEl.appendChild(b);
      });
    }

    // your print inside each room thumbnail (2D canvas over the thumb, clipped to the frame window)
    function paintThumbPrints() {
      if (!data || dead) return;
      scenesEl.querySelectorAll(".yxw-scene").forEach(b => {
        const sc = sceneById(b.dataset.id), th = b.querySelector(".yxw-thumb"); if (!th) return;
        let c = th.querySelector("canvas.yxw-tprint");
        if (!print) { if (c) c.remove(); return; }
        if (!c) { c = document.createElement("canvas"); c.className = "yxw-tprint"; c.setAttribute("aria-hidden", "true"); th.appendChild(c); }
        const W = 192, H = 240, k = W / sc.w; c.width = W; c.height = H;
        const x = c.getContext("2d"); x.clearRect(0, 0, W, H);
        sc.windows.forEach(w => {
          const q = w.corners.map(([px, py]) => [px * k, py * (H / sc.h)]);
          const xs = q.map(p => p[0]), ys = q.map(p => p[1]);
          const x0 = Math.min(...xs), y0 = Math.min(...ys), bw = Math.max(...xs) - x0, bh = Math.max(...ys) - y0;
          if (bw < 2 || bh < 2) return;
          try {
            const art = buildArt(print, w.aspect || bw / bh, Math.ceil(bw * 2), Math.ceil(bh * 2));
            x.save(); x.beginPath(); q.forEach((p, i) => i ? x.lineTo(p[0], p[1]) : x.moveTo(p[0], p[1])); x.closePath(); x.clip();
            x.drawImage(art, x0, y0, bw, bh); x.restore();
          } catch (e) { /* thumbnail stays as the empty room */ }
        });
      });
    }

    function syncUI() {
      if (!data) return;
      const s = sceneById(o.scene); o.scene = s.id;
      scenesEl.querySelectorAll(".yxw-scene").forEach(b => {
        const on = b.dataset.id === o.scene;
        b.setAttribute("aria-checked", on ? "true" : "false"); b.tabIndex = on ? 0 : -1;
        const sc = sceneById(b.dataset.id); const img = b.querySelector("img"); const src = base + sc.thumb[o.frame];
        if (img.getAttribute("src") !== src) img.src = src;
      });
      const info = SIZE_INFO[o.size];
      const fr = o.frame === "white" ? "White frame" : "Black frame";
      canvas.setAttribute("aria-label", "Your design in a " + o.frame + " frame, " + (s.size ? "size guide over a sofa" : s.label.toLowerCase()));
      if (s.size) {
        cap.textContent = "Both sizes at true scale over a sofa. Yours is the " + info.inch + ".";
        labels.innerHTML = "";
        s.windows.forEach(w => {
          const d = document.createElement("div");
          d.className = "yxw-sz" + (w.key === o.size ? " is-on" : "");
          const [[x0, y0], [x1]] = w.outer;
          d.style.left = ((x0 + x1) / 2 / s.w * 100) + "%"; d.style.top = (y0 / s.h * 100) + "%";
          d.innerHTML = "<b></b><span></span>";
          d.querySelector("b").textContent = SIZE_INFO[w.key].inch; d.querySelector("span").textContent = SIZE_INFO[w.key].cm;
          labels.appendChild(d);
        });
      } else {
        cap.textContent = "Your design, printed and framed. Pick a\u00a0room.";
        labels.innerHTML = "";
      }
      meta.textContent = fr + ", " + info.inch + " (" + info.cm + ")";
    }

    function canvasSize() {
      const w = stage.clientWidth || 320, dpr = Math.min(window.devicePixelRatio || 1, 3);
      const cw = Math.max(240, Math.min(1280, Math.round(w * dpr)));
      return [cw, Math.round(cw * 5 / 4)];
    }

    // background resampled once to the canvas size (sharper than GPU minification of a 1280 px texture)
    function bgFor(img, key, cw, ch) {
      const k = key + "@" + cw;
      if (bgCache.has(k)) return bgCache.get(k);
      let out = img;
      if (img.naturalWidth !== cw) {
        out = document.createElement("canvas"); out.width = cw; out.height = ch;
        const x = out.getContext("2d"); x.imageSmoothingQuality = "high"; x.drawImage(img, 0, 0, cw, ch);
      }
      bgCache.clear(); bgCache.set(k, out);
      return out;
    }

    function artFor(win, sw, cw) {
      const s = cw / sw;
      const wpx = Math.max(dist(win.corners[0], win.corners[1]), dist(win.corners[3], win.corners[2])) * s;
      const hpx = Math.max(dist(win.corners[0], win.corners[3]), dist(win.corners[1], win.corners[2])) * s;
      const tw = Math.min(2048, pot(wpx * 1.25)), th = Math.min(2048, pot(hpx * 1.25));
      const key = [printVer, win.aspect, tw, th].join("|");
      if (!artCache.has(key)) { if (artCache.size > 6) artCache.clear(); artCache.set(key, buildArt(print, win.aspect, tw, th)); }
      return { art: artCache.get(key), key };
    }

    function render() { cancelAnimationFrame(raf); raf = requestAnimationFrame(doRender); }

    async function doRender() {
      if (dead || !data) return;
      const my = ++seq;
      const s = sceneById(o.scene);
      const t0 = performance.now();
      const win = s.size ? s.windows.find(w => w.key === o.size) : s.windows[0];
      let bgImg, layImg;
      try {
        if (!stage.dataset.ready) setStatus("loading", "Loading room");
        [bgImg, layImg] = await Promise.all([loadImg(base + s.bg[o.frame]), loadImg(base + win.layers)]);
      } catch (e) { if (my === seq) setStatus("error", "Could not load this room. Check your connection and try again."); return; }
      if (dead || my !== seq) return;
      const [cw, ch] = canvasSize();
      if (canvas.width !== cw || canvas.height !== ch) { canvas.width = cw; canvas.height = ch; }
      if (!renderer || renderer.lost()) {
        try { renderer = makeGL(canvas); } catch (e) { renderer = null; }
        if (!renderer) renderer = make2D(canvas);
      }
      const { art, key: artKey } = artFor(win, s.w, cw);
      if (!win._hinv) {
        win._hinv = inv3(squareToQuad(win.corners)); win._edges = edgeLines(win.corners);
      }
      renderer.draw({
        bg: bgFor(bgImg, s.bg[o.frame], cw, ch), bgKey: s.bg[o.frame] + "@" + cw, sw: s.w, sh: s.h,
        art, artKey,
        win: { q: win.corners, hinv: win._hinv, edges: win._edges, bbox: win.bbox, lay: layImg, layKey: win.layers, veil: win.veil, grain: win.grain }
      });
      // size guide: the customer's print goes in the other size's frame too, not a blank paper (M13)
      if (s.size && print) {
        for (const w2 of s.windows) {
          if (w2 === win) continue;
          let lay2; try { lay2 = await loadImg(base + w2.layers); } catch (e) { continue; }
          if (dead || my !== seq) return;
          if (!w2._hinv) { w2._hinv = inv3(squareToQuad(w2.corners)); w2._edges = edgeLines(w2.corners); }
          const a2 = artFor(w2, s.w, cw);
          renderer.draw({ keep: true, bg: bgFor(bgImg, s.bg[o.frame], cw, ch), bgKey: s.bg[o.frame] + "@" + cw, sw: s.w, sh: s.h, art: a2.art, artKey: a2.key,
            win: { q: w2.corners, hinv: w2._hinv, edges: w2._edges, bbox: w2.bbox, lay: lay2, layKey: w2.layers, veil: w2.veil, grain: w2.grain } });
        }
      }
      stage.dataset.ready = "1";
      setStatus("ready", "");
      api.lastRenderMs = Math.round(performance.now() - t0);
      el.dispatchEvent(new CustomEvent("yxw:render", { detail: { scene: s.id, frame: o.frame, size: o.size, ms: api.lastRenderMs } }));
      // warm the other rooms in this frame colour once the first one is up
      if (!api._warmed) { api._warmed = true; data.scenes.forEach(x => { loadImg(base + x.bg[o.frame]).catch(() => {}); x.windows.forEach(w => loadImg(base + w.layers).catch(() => {})); }); }
    }

    async function loadPrint() {
      const v = ++printVer;
      try { const p = await resolvePrint(o); if (v !== printVer || dead) return; print = p; }
      catch (e) { if (v === printVer) print = null; console.warn("[YXIWallView] print failed", e); }
      artCache.clear(); render(); paintThumbPrints();
    }

    const ro = window.ResizeObserver ? new ResizeObserver(() => render()) : null;
    if (ro) ro.observe(stage); else window.addEventListener("resize", render);

    const api = {
      lastRenderMs: 0,
      update(next) {
        if (dead || !next) return;
        const reprint = "getPreviewCanvas" in next || "previewDataURL" in next || next.refreshPrint;
        if ("getPreviewCanvas" in next) { o.getPreviewCanvas = next.getPreviewCanvas; if (next.getPreviewCanvas) delete o.previewDataURL; }
        if ("previewDataURL" in next) { o.previewDataURL = next.previewDataURL; if (next.previewDataURL) delete o.getPreviewCanvas; }
        if (next.frame) o.frame = normFrame(next.frame);
        if (next.size) o.size = normSize(next.size);
        if (next.scene) o.scene = next.scene;
        if ("onClose" in next) o.onClose = next.onClose;
        syncUI();
        if (reprint) loadPrint(); else render();
      },
      destroy() {
        dead = true; cancelAnimationFrame(raf);
        if (ro) ro.disconnect(); else window.removeEventListener("resize", render);
        el.removeEventListener("keydown", onKey);
        if (renderer) renderer.destroy();
        artCache.clear(); bgCache.clear();
        el.innerHTML = ""; el.classList.remove("yxw");
      }
    };

    loadJSON(base).then(d => {
      if (dead) return;
      data = d; buildThumbs(); syncUI(); loadPrint();
    }).catch(() => setStatus("error", "Could not load the room previews."));
    return api;
  }

  function preload(assetsBase, frame) {
    const base = assetsBase ? new URL(assetsBase, location.href).href : DEFAULT_BASE;
    const f = normFrame(frame);
    return loadJSON(base).then(d => Promise.all(d.scenes.map(s => Promise.all([loadImg(base + s.bg[f]), ...s.windows.map(w => loadImg(base + w.layers))])))).catch(() => {});
  }

  window.YXIWallView = { mount, preload, version: 1 };
})();
